import React, { useMemo } from 'react';

const LoginHeatmap = ({ data }) => {
  // Transform data into pivot table structure
  const pivotData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Create a map for quick lookup: key = "estHour_day", value = avg_logins
    const dataMap = {};
    let minValue = Infinity;
    let maxValue = -Infinity;

    data.forEach(item => {
      const utcHour = Math.round(item.hour);
      const day = Math.round(item.day_of_week);
      
      // Convert UTC to EST: if (hour > 4, hour - 4, hour + 20)
      let estHour = utcHour > 4 ? utcHour - 4 : utcHour + 20;
      
      // Normalize hour 24 to 0 (12AM)
      if (estHour >= 24) {
        estHour = estHour % 24;
      }
      
      const key = `${estHour}_${day}`;
      
      // Sum up avg_logins for the same hour/day combination (across all countries and site_roles)
      if (!dataMap[key]) {
        dataMap[key] = 0;
      }
      dataMap[key] += item.avg_logins;
    });
    
    // Track min/max for color scaling after all data is processed
    Object.values(dataMap).forEach(value => {
      if (value > maxValue) maxValue = value;
      if (value < minValue) minValue = value;
    });

    // Debug: Log the data for 12AM to verify
    console.log('12AM (hour 0) data across all days:', {
      Sunday: dataMap['0_0'],
      Monday: dataMap['0_1'],
      Tuesday: dataMap['0_2'],
      Wednesday: dataMap['0_3'],
      Thursday: dataMap['0_4'],
      Friday: dataMap['0_5'],
      Saturday: dataMap['0_6']
    });

    // Build the pivot table structure - start from 1AM to 12AM (like Looker Studio)
    const hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0];
    const days = [0, 1, 2, 3, 4, 5, 6]; // Sunday to Saturday

    const pivotTable = hours.map(hour => {
      const row = { hour };
      days.forEach(day => {
        const key = `${hour}_${day}`;
        row[day] = Math.round(dataMap[key] || 0);
      });
      return row;
    });

    return { pivotTable, minValue, maxValue };
  }, [data]);

  // Format hour for display (0 -> 12AM, 1 -> 1AM, 13 -> 1PM, etc.)
  const formatHour = (hour) => {
    if (hour === 0) return '12AM';
    if (hour === 12) return '12PM';
    if (hour < 12) return `${hour}AM`;
    return `${hour - 12}PM`;
  };

  // Get color based on value (blue gradient like Looker Studio)
  const getColor = (value, min, max) => {
    if (value === 0) return 'rgb(225, 235, 245)'; // Very light blue
    
    // Normalize value between 0 and 1
    const normalized = (value - min) / (max - min);
    
    // Create blue gradient from light to dark
    // Light blue: rgb(187, 222, 251) to Dark blue: rgb(30, 136, 229)
    const r = Math.round(187 - normalized * 157);
    const g = Math.round(222 - normalized * 86);
    const b = Math.round(251 - normalized * 22);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Get text color based on background (dark backgrounds need white text)
  const getTextColor = (value, min, max) => {
    if (value === 0) return '#333';
    const normalized = (value - min) / (max - min);
    return normalized > 0.6 ? '#ffffff' : '#333333';
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (!pivotData) {
    return <div className="text-center py-8">No data available</div>;
  }

  const { pivotTable, minValue, maxValue } = pivotData;

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse table-fixed" style={{ fontSize: '13px' }}>
        <thead>
          <tr>
            <th 
              className="border border-gray-300 p-3 text-left font-semibold bg-gray-100 w-28"
            >
              Time in EST
            </th>
            {dayNames.map((day) => (
              <th 
                key={day} 
                className="border border-gray-300 p-3 text-center font-semibold bg-gray-100"
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pivotTable.map((row) => (
            <tr key={row.hour}>
              <td 
                className="border border-gray-300 p-3 font-medium bg-white w-28"
              >
                {formatHour(row.hour)}
              </td>
              {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                const value = row[day];
                const bgColor = getColor(value, minValue, maxValue);
                const textColor = getTextColor(value, minValue, maxValue);
                
                return (
                  <td
                    key={day}
                    className="border border-gray-300 p-3 text-center font-medium transition-all hover:opacity-80 cursor-pointer"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                    title={`${dayNames[day]} ${formatHour(row.hour)}: ${value} average logins`}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LoginHeatmap;

